import React, { useState, useEffect } from 'react';
import { Text } from '@smarterservices/smarter-ui';

/**
 * TimeElapsedCounter Component
 * 
 * Displays the elapsed time between two dates in "h m s" format.
 * If no toDate is provided, it assumes the current time and updates every second.
 * 
 * @component
 * @param {Object} props
 * @param {Date|string|number} props.fromDate - The start date (Date object, ISO string, or timestamp)
 * @param {Date|string|number} [props.toDate] - The end date (optional, defaults to current time)
 * @param {...Object} props - All other props are passed to the Text component
 * 
 * @example
 * // Static time display
 * <TimeElapsedCounter 
 *   fromDate={new Date('2024-01-01T10:00:00')}
 *   toDate={new Date('2024-01-01T12:30:45')}
 *   size="medium"
 *   weight="bold"
 * />
 * 
 * @example
 * // Live updating counter
 * <TimeElapsedCounter 
 *   fromDate={new Date('2024-01-01T10:00:00')}
 *   color="success"
 * />
 */
const TimeElapsedCounter = ({ fromDate, toDate, ...textProps }) => {
    const [elapsedTime, setElapsedTime] = useState('');

    /**
     * Calculates the elapsed time between two dates
     * @param {Date} from - Start date
     * @param {Date} to - End date
     * @returns {string} Formatted time string in "h m s" format
     */
    const calculateElapsedTime = (from, to) => {
        const fromTime = new Date(from).getTime();
        const toTime = new Date(to).getTime();

        // Calculate difference in milliseconds
        const diffMs = toTime - fromTime;

        // Handle negative time (if toDate is before fromDate)
        if (diffMs < 0) {
            return null;
        }

        // Convert to seconds, minutes, hours
        const totalSeconds = Math.floor(diffMs / 1000);
        const hours = Math.floor(totalSeconds / 3600);
        const minutes = Math.floor((totalSeconds % 3600) / 60);
        const seconds = totalSeconds % 60;

        // Build time string, only showing non-zero values
        const parts = [];
        if (hours > 0) parts.push(`${hours}h`);
        if (minutes > 0) parts.push(`${minutes}m`);
        if (seconds > 0 || parts.length === 0) parts.push(`${seconds}s`);

        return parts.join(' ');
    };

    useEffect(() => {
        // Calculate initial elapsed time
        const updateElapsedTime = () => {
            const to = toDate ? new Date(toDate) : new Date();
            const elapsed = calculateElapsedTime(fromDate, to);
            setElapsedTime(elapsed);
        };

        // Update immediately
        updateElapsedTime();

        // If no toDate, set up interval to update every second
        let interval;
        if (!toDate) {
            interval = setInterval(updateElapsedTime, 1000);
        }

        // Cleanup interval on unmount
        return () => {
            if (interval) {
                clearInterval(interval);
            }
        };
    }, [fromDate, toDate]);

    return <Text {...textProps}>{elapsedTime}</Text>;
};

export default TimeElapsedCounter;
